
import { computed, defineComponent, inject, nextTick, PropType, ref, watch } from 'vue';
import { useOnOutsidePress } from 'vue-composable';
import { cloneDeep, isEqual } from 'lodash';
import { Option } from '@/types';
import { toLowercaseAndRemoveWhitespace } from '@/utils';
import MaterialIcon from '@/components/MaterialIcon.vue';
import { InjectionKeys } from '@/constants';

export default defineComponent({
  name: 'FilterableSelect',
  emits: ['update:modelValue', 'input'],
  components: {
    MaterialIcon
  },
  props: {
    label: {
      type: String,
      required: false,
      default: ''
    },
    options: {
      type: Array as PropType<Array<Option<unknown>>>,
      required: true
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Select an option...'
    },
    modelValue: {
      type: [Object, String]
    },
    filterable: {
      type: Boolean,
      required: false,
      default: false
    },
    required: {
      type: Boolean
    },
    sortOptionsAlphabetically: {
      type: Boolean,
      default: true
    },
    highlightDiv: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const selectRef = ref<HTMLDivElement>();
    const filterInputRef = ref<HTMLInputElement>();
    const show = ref(false);

    // Filtering
    const filterInput = ref('');
    const optionIncludesValue = (option: Option<unknown>, include: string) =>
      toLowercaseAndRemoveWhitespace(option.label as string).includes(toLowercaseAndRemoveWhitespace(include));
    const filteredOptions = computed(() =>
      props.options
        .filter(option => optionIncludesValue(option, filterInput.value))
        .slice()
        .sort((a, b) => {
          if (!props.sortOptionsAlphabetically) return 0;
          if (a.label < b.label) return -1;
          if (b.label < a.label) return 1;
          return 0;
        })
    );

    // Display
    const isOpen = computed(() => show.value);
    const open = () => {
      show.value = true;
      nextTick(() => filterInputRef.value?.focus());
    };

    const close = () => {
      nextTick(() => filterInputRef.value?.blur());
      filterInput.value = '';
      show.value = false;
    };

    const toggle = () => {
      isOpen.value ? close() : open();
    };

    useOnOutsidePress(selectRef, close);

    // Selection - selectedOption only updates when the id of the modelValue changes
    const selectedOption = computed(() => {
      return cloneDeep(
        props.options.find(option => {
          return isEqual(option.value, props.modelValue);
        })
      );
    });

    const selected = ref(selectedOption.value);
    const hasSelected = computed(() => !!selected.value);
    const selectAndClose = (option: Option<unknown>) => {
      selected.value = option;
      close();
    };

    const setFormDirty = inject(InjectionKeys.SetFormDirty);

    watch(selected, () => {
      emit('update:modelValue', props.options.find(option => isEqual(option, selected.value))?.value);
      setFormDirty?.();
    });

    watch(
      () => selectedOption.value,
      newVal => {
        if (newVal === undefined) return;
        if (!isEqual(selected.value, newVal)) {
          selected.value = newVal;
        }
      }
    );

    const showSelectedValue = computed(() => (props.filterable ? !isOpen.value && hasSelected.value : hasSelected.value));
    const showFilterInput = computed(() => props.filterable && isOpen.value);

    return {
      selectRef,
      filterInputRef,
      show,
      open,
      toggle,
      selectAndClose,
      selected,
      hasSelected,
      isOpen,
      filterInput,
      filteredOptions,
      showSelectedValue,
      showFilterInput
    };
  }
});
